/**
 * Modal flow for adding a vehicle (or stock-only item) ahead of the
 * vendor feed. Single screen — VIN and Stock # are both visible from
 * the start, and either is sufficient.
 *
 * vPIC decode fires automatically the moment VIN reaches 17 characters
 * and re-fires on subsequent edits to a different 17-char VIN. The
 * year/make/model fields auto-populate from the decode and remain
 * editable so the operator can correct anything vPIC got wrong.
 *
 * Submission is blocked until: a dealer is picked AND at least one of
 * VIN or Stock # is non-empty.
 *
 * Modal pattern matches the inline fixed-position dialogs used in
 * UserManagementPage / VendorCredentialForm — there is no shared Dialog
 * component yet in INVI.
 */
import React, { useEffect, useRef, useState } from "react"

import { THEME } from "../../config/theme"
import { tint } from "../../utils/color"
import {
  createSession,
  decodeVin,
} from "../../api/photoCapture"
import { RooftopOption } from "../../api/imageIndex"

interface Props {
  rooftops: RooftopOption[]
  defaultRooftopSlug: string | null
  onClose: () => void
  /**
   * Fired after the session is created. `routeKey` tells the caller how
   * the new row should be reached:
   *   { kind: "vin",   value: "..." }  → navigate to /photos/vehicles/:vin
   *   { kind: "stock", value: "..." }  → navigate to /photos/items/:stock
   */
  onCreated: (
    routeKey: { kind: "vin"; value: string } | { kind: "stock"; value: string },
    rooftopSlug: string,
  ) => void
}

const VIN_LENGTH = 17
const MOBILE_BREAKPOINT = 768

export default function AddVehicleModal({
  rooftops,
  defaultRooftopSlug,
  onClose,
  onCreated,
}: Props) {
  const isMobile =
    typeof window !== "undefined" && window.innerWidth < MOBILE_BREAKPOINT
  const [vin, setVin] = useState<string>("")
  const [stock, setStock] = useState<string>("")
  const [rooftopSlug, setRooftopSlug] = useState<string>(defaultRooftopSlug ?? "")
  const [year, setYear] = useState<string>("")
  const [make, setMake] = useState<string>("")
  const [model, setModel] = useState<string>("")

  const [decoding, setDecoding] = useState<boolean>(false)
  const [decodeNotice, setDecodeNotice] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState<boolean>(false)

  // Tracks which VINs we've already decoded so editing back-and-forth
  // doesn't refire NHTSA on every keystroke. Re-decode only when the
  // 17-char VIN changes to a NEW 17-char VIN.
  const lastDecodedVinRef = useRef<string | null>(null)

  const normalizedVin = vin.trim().toUpperCase()
  const hasVin = normalizedVin.length > 0
  const hasStock = stock.trim().length > 0
  const canSubmit = !!rooftopSlug && (hasVin || hasStock) && !submitting

  useEffect(() => {
    if (normalizedVin.length !== VIN_LENGTH) {
      lastDecodedVinRef.current = null
      return
    }
    if (lastDecodedVinRef.current === normalizedVin) return
    let cancelled = false
    lastDecodedVinRef.current = normalizedVin
    setDecoding(true)
    setDecodeNotice(null)
    ;(async () => {
      try {
        const result = await decodeVin(normalizedVin)
        if (cancelled) return
        if (result.year !== null) setYear(String(result.year))
        if (result.make) setMake(result.make)
        if (result.model) setModel(result.model)
        if (result.year === null && !result.make && !result.model) {
          setDecodeNotice(
            "We couldn't auto-fill from this VIN. Type the details below manually.",
          )
        }
      } catch {
        if (!cancelled) {
          setDecodeNotice(
            "VIN lookup failed. You can still fill year, make, and model manually.",
          )
        }
      } finally {
        if (!cancelled) setDecoding(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [normalizedVin])

  const handleSubmit = async () => {
    setSubmitError(null)
    if (!rooftopSlug) {
      setSubmitError("Pick a dealer.")
      return
    }
    if (!hasVin && !hasStock) {
      setSubmitError("Enter a VIN, a stock number, or both.")
      return
    }
    const parsedYear = year.trim() === "" ? null : Number.parseInt(year, 10)
    if (parsedYear !== null && Number.isNaN(parsedYear)) {
      setSubmitError("Year must be a number.")
      return
    }
    setSubmitting(true)
    try {
      await createSession({
        rooftop_slug: rooftopSlug,
        vin: hasVin ? normalizedVin : null,
        stock: hasStock ? stock.trim() : null,
        client_session_uuid: crypto.randomUUID(),
        year: parsedYear,
        make: make.trim() || null,
        model: model.trim() || null,
      })
      if (hasVin) {
        onCreated({ kind: "vin", value: normalizedVin }, rooftopSlug)
      } else {
        onCreated({ kind: "stock", value: stock.trim() }, rooftopSlug)
      }
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Could not add vehicle",
      )
      setSubmitting(false)
    }
  }

  return (
    <div style={backdropStyle} onClick={onClose}>
      <div
        style={{
          ...dialogStyle,
          ...(isMobile && {
            minWidth: 0,
            width: "calc(100vw - 24px)",
            padding: THEME.spacing.md,
          }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={headerStyle}>
          <h2 style={titleStyle}>Add Vehicle</h2>
          <button type="button" onClick={onClose} style={closeButtonStyle}>
            ×
          </button>
        </div>

        <p style={hintStyle}>
          Use this when a vehicle on the lot isn&apos;t in the vendor feed
          yet — or for inventory that doesn&apos;t carry a VIN at all
          (campers, trailers, accessory bundles). Enter a VIN, a stock
          number, or both.
        </p>

        <div style={rowFieldsStyle}>
          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>
              VIN {decoding && <span style={inlineStatusStyle}> — looking up…</span>}
            </span>
            <input
              type="text"
              value={vin}
              onChange={(e) => setVin(e.target.value)}
              placeholder="17-character VIN"
              style={inputStyle}
              maxLength={17}
              autoFocus
            />
            {hasVin && (
              <span style={fieldHintStyle}>
                {normalizedVin.length}/17 characters
              </span>
            )}
          </label>

          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Stock #</span>
            <input
              type="text"
              value={stock}
              onChange={(e) => setStock(e.target.value)}
              placeholder="Internal stock number"
              style={inputStyle}
            />
          </label>
        </div>

        {decodeNotice && <div style={noticeStyle}>{decodeNotice}</div>}

        <label style={fieldStyle}>
          <span style={fieldLabelStyle}>Dealer</span>
          <select
            value={rooftopSlug}
            onChange={(e) => setRooftopSlug(e.target.value)}
            style={inputStyle}
          >
            <option value="">— pick a dealer —</option>
            {rooftops.map((d) => (
              <option key={d.slug} value={d.slug}>
                {d.name}
              </option>
            ))}
          </select>
        </label>

        <div
          style={{
            ...rowFieldsStyle,
            ...(isMobile && { flexDirection: "column" }),
          }}
        >
          <label
            style={{
              ...fieldStyle,
              ...(isMobile ? { flex: 1 } : { flex: "0 0 100px" }),
            }}
          >
            <span style={fieldLabelStyle}>Year</span>
            <input
              type="text"
              inputMode="numeric"
              value={year}
              onChange={(e) => setYear(e.target.value)}
              style={inputStyle}
              maxLength={4}
            />
          </label>

          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Make</span>
            <input
              type="text"
              value={make}
              onChange={(e) => setMake(e.target.value)}
              style={inputStyle}
            />
          </label>

          <label style={{ ...fieldStyle, flex: 1 }}>
            <span style={fieldLabelStyle}>Model</span>
            <input
              type="text"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              style={inputStyle}
            />
          </label>
        </div>

        {submitError && <div style={errorStyle}>{submitError}</div>}

        <div style={footerStyle}>
          <button type="button" onClick={onClose} style={secondaryButtonStyle}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={primaryButtonStyle(canSubmit)}
          >
            {submitting ? "Adding…" : "Add Vehicle"}
          </button>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const backdropStyle: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.4)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}

const dialogStyle: React.CSSProperties = {
  background: THEME.colors.neutral.light.surface,
  borderRadius: THEME.borderRadius.lg,
  padding: THEME.spacing.lg,
  minWidth: 520,
  maxWidth: 620,
  maxHeight: "90vh",
  overflowY: "auto",
  fontFamily: THEME.fonts.primary,
  boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
}

const headerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  marginBottom: THEME.spacing.sm,
}

const titleStyle: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  color: THEME.colors.neutral.light.textPrimary,
  margin: 0,
}

const closeButtonStyle: React.CSSProperties = {
  background: "transparent",
  border: "none",
  fontSize: 24,
  cursor: "pointer",
  color: THEME.colors.neutral.light.textSecondary,
  lineHeight: 1,
}

const hintStyle: React.CSSProperties = {
  fontSize: 13,
  color: THEME.colors.neutral.light.textSecondary,
  margin: 0,
  marginBottom: THEME.spacing.md,
}

const fieldStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: THEME.spacing.xs,
  marginBottom: THEME.spacing.md,
}

const rowFieldsStyle: React.CSSProperties = {
  display: "flex",
  gap: THEME.spacing.sm,
}

const fieldLabelStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 500,
  letterSpacing: "0.5px",
  textTransform: "uppercase",
  color: THEME.colors.neutral.light.textSecondary,
}

const inlineStatusStyle: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 400,
  textTransform: "none",
  letterSpacing: 0,
  color: THEME.colors.neutral.light.textSecondary,
}

const fieldHintStyle: React.CSSProperties = {
  fontSize: 11,
  color: THEME.colors.neutral.light.textSecondary,
}

const inputStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  border: `1px solid ${THEME.colors.silver}`,
  borderRadius: THEME.borderRadius.md,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  fontFamily: THEME.fonts.primary,
}

const noticeStyle: React.CSSProperties = {
  fontSize: 12,
  background: tint(THEME.auditTable.tabColors.pending, 0.5),
  border: `1px solid ${THEME.auditTable.tabColors.pending}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  color: THEME.colors.neutral.light.textPrimary,
  marginBottom: THEME.spacing.md,
}

const errorStyle: React.CSSProperties = {
  fontSize: 12,
  background: tint(THEME.colors.semantic.danger, 0.1),
  border: `1px solid ${THEME.colors.semantic.danger}`,
  borderRadius: THEME.borderRadius.md,
  padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
  color: THEME.colors.semantic.danger,
  marginBottom: THEME.spacing.md,
}

const footerStyle: React.CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  gap: THEME.spacing.sm,
}

const primaryButtonStyle = (enabled: boolean): React.CSSProperties => ({
  fontSize: 13,
  fontWeight: 500,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  borderRadius: THEME.borderRadius.md,
  border: "none",
  background: enabled ? THEME.colors.navy : THEME.colors.silver,
  color: THEME.colors.white,
  cursor: enabled ? "pointer" : "not-allowed",
  fontFamily: THEME.fonts.primary,
})

const secondaryButtonStyle: React.CSSProperties = {
  fontSize: 13,
  padding: `${THEME.spacing.xs} ${THEME.spacing.md}`,
  borderRadius: THEME.borderRadius.md,
  border: `1px solid ${THEME.colors.silver}`,
  background: THEME.colors.neutral.light.surface,
  color: THEME.colors.neutral.light.textPrimary,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
}
